import { useEffect, useState } from 'react';
import type { AuthUser } from '../api/auth';
import './LoginGate.css';

export type GateReason = 'guest-limit' | 'subscriber-only' | 'login';

interface Props {
  isOpen: boolean;
  reason: GateReason;
  isSubmitting: boolean;
  error: string | null;
  user: AuthUser | null;
  channelName: string;
  onClose: () => void;
  onLogin: (email: string, password: string) => Promise<void>;
}

function getCopy(reason: GateReason, user: AuthUser | null, channelName: string) {
  if (reason === 'guest-limit') {
    return {
      title: 'Free guest plays used',
      description: `You have used all 5 guest plays for ${channelName}. Login to keep listening.`,
      submitLabel: 'Login to continue',
    };
  }

  if (user) {
    return {
      title: 'Subscriber-only content',
      description: `This channel has locked messages. Subscribe in the SecretTalk app to unlock everything in ${channelName}.`,
      submitLabel: '',
    };
  }

  return {
    title: reason === 'login' ? 'Login to SecretTalk' : 'Login for subscriber access',
    description: `Login with your SecretTalk email account to continue listening to ${channelName}.`,
    submitLabel: 'Login',
  };
}

export function LoginGate({
  isOpen,
  reason,
  isSubmitting,
  error,
  user,
  channelName,
  onClose,
  onLogin,
}: Props) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) {
      setPassword('');
      setValidationError(null);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const copy = getCopy(reason, user, channelName);
  const needsLogin = !user;

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!email || !password) {
      setValidationError('Email and password are required.');
      return;
    }
    setValidationError(null);
    await onLogin(email, password);
  };

  return (
    <div className="login-gate-overlay" onClick={onClose}>
      <div className="login-gate-modal" onClick={(event) => event.stopPropagation()}>
        <button type="button" className="login-gate-close" onClick={onClose} aria-label="Close modal">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        <div className="login-gate-lock" aria-hidden="true">
          <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
            <rect x="3" y="11" width="18" height="10" rx="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </svg>
        </div>

        <h2 className="login-gate-title">{copy.title}</h2>
        <p className="login-gate-desc">{copy.description}</p>

        {needsLogin ? (
          <form className="login-gate-form" onSubmit={handleSubmit}>
            <label className="login-gate-field">
              <span>Email</span>
              <input
                type="email"
                autoComplete="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="you@example.com"
              />
            </label>
            <label className="login-gate-field">
              <span>Password</span>
              <input
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                placeholder="Password"
              />
            </label>

            {validationError || error ? <p className="login-gate-error">{validationError || error}</p> : null}

            <button type="submit" className="login-gate-btn login-gate-btn--primary" disabled={isSubmitting}>
              {isSubmitting ? 'Logging in...' : copy.submitLabel}
            </button>
          </form>
        ) : (
          <div className="login-gate-actions">
            <a
              className="login-gate-btn login-gate-btn--primary"
              href="https://secrettalk.app/download"
              target="_blank"
              rel="noreferrer"
            >
              Open app to subscribe
            </a>
            <button type="button" className="login-gate-btn login-gate-btn--secondary" onClick={onClose}>
              Keep previewing
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
